import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import type {OmitStrict} from '@wix/devzai-utils-common';
import {arrayFlatten, arrayRemoveNullValues, asArray, immerProduce, hideableValueCreateHidden, hideableValueIsVisibleAndNotEqualValue } from '@wix/devzai-utils-common';
import {
    BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    doppeListActionContentSettingsMetadataCreateForListActionCommonSettings,
    DoppeListActionSettings
} from '../../client-server-common';
import {
    doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails,
    doppeListActionItemCtaButtonsResetSettingsForLock,
    doppeListActionListItemCalculateItemButtons,
    doppeListActionSettingsFilterHiddenListItemsFromActionView,
    doppeListActionWithMainDetailsGetMainImage,
    doppeListActionWithMainDetailsReplaceMainImage,
} from '../../client-server-common';
import {
    DoppeActionContentSectionsListItemSettingsMetadata,
    DoppeActionContentSectionType,
} from '../../client-server-common/types/doppe-action-content-section';
import {doppeActionCtaButtonsResetSettingsForLock} from '../../client-server-common/types/doppe-action-cta-button';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import type {DoppeActionTypeTestimonial} from '../doppe-action-type-testimonial/doppe-action-type-testimonial';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import {doppeActionTypeTestimonialsListResolveActionSettings} from './doppe-action-type-testimonials-list-utils';


export namespace DoppeActionTypeTestimonialsList {


    export interface TestimonialItemBaseSettings extends DoppeListActionSettings.ListItemBaseSettings,
        OmitStrict<DoppeActionTypeTestimonial.ActionSettings, 'sections' | 'ctaButtons'> {
    }

    export interface TestimonialItemSettings extends DoppeListActionSettings.ListItemWithSections,
        DoppeListActionSettings.ListItemWithCtaButtons,
        TestimonialItemBaseSettings {
    }

    export interface ActionSettings extends DoppeListActionSettings<DoppeActionTypeTestimonialsList.TestimonialItemSettings>,
        DoppeActionContentSectionsSettings,
        DoppeListActionSettings.WithSearchSettings,
        DoppeListActionSettings.WithMainDetails,
        DoppeActionCtaButtonsSettings,
        DoppeListActionSettings.WithItemClickBehaviour {
        itemShowRating: boolean;
        previewShowDescription: boolean;
    }

}

export const doppeActionTypeTestimonialsList = doppeActionTypeDefineMetadata<DoppeActionTypeTestimonialsList.ActionSettings>({
    id: 'co.hopp.action/testimonials-list',
    resolveActionSettings: action => {
        return doppeActionTypeTestimonialsListResolveActionSettings(action.settings);
    },
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            doppeListActionItemCtaButtonsResetSettingsForLock(actionSettings);
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            ...doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails(),
            ...doppeListActionContentSettingsMetadataCreateForListActionCommonSettings(),
            itemShowRating: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.itemShowRating'}),
            previewShowDescription: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.previewShowDescription'}),

        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
            mainImage: true,
            mainTitle: true
        },
        bindableContentSettingsNames: {}
    },
    actionListItemSettingsMetadata: DoppeActionContentSectionsListItemSettingsMetadata[DoppeActionContentSectionType.Testimonials],
    isWidgetOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    isPaymentAction: false,
    isFormAction: false,
    isListItemLinkOpener: false,
    supportsListLayout: true,
    resolveUsedMedia: actionSettings => {
        return arrayRemoveNullValues([...arrayFlatten(actionSettings.listDataItems.map(itemSettings => {
            return itemSettings.profileMedia ? asArray(itemSettings.profileMedia) : []
        })), hideableValueIsVisibleAndNotEqualValue(actionSettings.mainImage, null) ? actionSettings.mainImage : null]);
    },
    resolveMainMedia: actionSettings => {
        return doppeListActionWithMainDetailsGetMainImage(actionSettings, actionSettings.listDataItems[0]?.profileMedia ?? null);
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: null,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps
        }) ?? null;
    },
    resolveListItemsCount: actionSettings => {
        return doppeListActionSettingsFilterHiddenListItemsFromActionView(actionSettings.listDataItems).length;
    },
    resolveListItems: (action, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        const actionSettings = action.settings;
        return actionSettings.listDataItems.map(item => {
            return {
                url: doppeActionCtaButtonsResolveMainURL(
                    doppeListActionListItemCalculateItemButtons(actionSettings.listItemTemplateCtaButtonsSettings, item), {
                        sourceLinkSpec: null,
                        viewerLang: viewerLang,
                        bymoPageProps: bymoPageProps,
                        viewerPagesRoutesManager: viewerPagesRoutesManager,
                        bymoPageContextProps: bymoPageContextProps
                    }) ?? null,
                title: item.name,
                action: action,
                couponCode: hideableValueCreateHidden(''),
                description: item.description,
                image: item.profileMedia ? asArray(item.profileMedia)[0] : null,
                itemId: item.id,
                ribbon: item.ribbon,
                enabled: item.enabled,
                deleted: item.deleted,
                displayInSearch: item.displayInSearch
            }
        });
    },
    resolveListSearchSettings: actionSettings => {
        return actionSettings.listSearchSettings;
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            for (const item of actionSettings.listDataItems) {
                if (item.profileMedia) {
                    item.profileMedia = wixMediaResourceReplaceExternalImage(item.profileMedia, externalImagesUrlToImageResourceMap)
                }
            }

            doppeListActionWithMainDetailsReplaceMainImage(actionSettings, externalImagesUrlToImageResourceMap);
        })
    },
});
